import $ from 'jquery';
import { showAlert } from '../core/alerts.js';

// CONCEPT: The browser half of the view-as context (#1511, ADR-0004). The server identifies a
//          customer view by an opaque id carried in the `vc` query parameter; a request that
//          arrives without it is the staff member, and one naming a context the server cannot
//          honour is refused outright. So every application request a page under a context
//          makes - links, form actions, redirects the page performs itself, XHR - has to carry
//          the id, and the id has to come from the page the server rendered, never from the
//          address bar (a fragment or a stale tab can disagree with what the server saw).
//
//          The layouts publish the id as <input type="hidden" id="vc"> and as
//          <meta name="pace-view-context">, both only while a context is active. This module
//          reads it from there and is the ONE place a `vc` is ever appended: withViewContext()
//          for a url, viewContextHiddenField() for a GET form (a GET submit replaces the
//          action's query string, so only a field survives), and a jQuery ajaxPrefilter that
//          tags every same-origin XHR so call sites need not remember to. It never tags a
//          cross-origin url, a non-http scheme, a fragment-only href or an asset path, and it
//          never duplicates: an existing `vc` is replaced.
//
//          There is no DOM sweep. The previous engine rewrote every href on the page after
//          load, which meant a link inserted a moment later was the staff member's; #1518
//          removed it once every producer - PHP views through view_as_url(), JS through
//          withViewContext() - built its urls with the context already on them.
//
// USE:     import { withViewContext } from '../ui/view-context.js';
//          $.ajax({ url: withViewContext('order/remove_item_from_order') })  // or let the
//          prefilter do it; window.location.href = withViewContext(url); GET forms include
//          viewContextHiddenField(). core/shell.js installs the prefilter and exposes
//          window.withViewContext for inline scripts.

export const VIEW_CONTEXT_PARAM = 'vc';

/**
 * The active context id the server rendered into this page, or '' when there is none.
 *
 * @returns {string}
 */
export function getViewContextId() {
    var input = document.getElementById(VIEW_CONTEXT_PARAM);

    if (input && typeof input.value === 'string' && input.value !== '') {
        return input.value;
    }

    var meta = document.querySelector('meta[name="pace-view-context"]');

    return meta ? String(meta.getAttribute('content') || '') : '';
}

function isExternal(url) {
    if (!/^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(url)) {
        return false;
    }

    try {
        return new URL(url, window.location.href).origin !== window.location.origin;
    } catch {
        return true;
    }
}

/**
 * Paths under the SITE ROOT that are assets, never application requests. The same three as
 * App\Libraries\ViewAs::ASSET_PREFIXES, in the same shape, because the two halves have to
 * answer identically: a url PHP tags and JS does not is a link that loses the context, and one
 * JS tags and PHP does not puts the identifier on a static file request.
 */
var ASSET_PREFIXES = ['/build/', '/local_assets/', '/uploads/'];

/**
 * The site's own base path, '' on a root-hosted site and e.g. '/shop' under a subdirectory.
 *
 * From __PAGE_CTX__.baseUrl, which every layout emits as base_url(), with <base href> as the
 * fallback because the layouts emit that too and it is what the browser itself resolves a
 * relative href against.
 *
 * @returns {string}
 */
function sitePathBase() {
    var ctx = window.__PAGE_CTX__;
    var raw = ctx && typeof ctx.baseUrl === 'string' ? ctx.baseUrl : '';

    if (raw === '') {
        var tag = document.querySelector('base[href]');
        raw = tag ? String(tag.getAttribute('href') || '') : '';
    }

    if (raw === '') {
        return '';
    }

    try {
        return new URL(raw, window.location.href).pathname.replace(/\/+$/, '');
    } catch {
        return '';
    }
}

/**
 * Where an asset prefix would have to start for $pathname to be an asset - the mirror of
 * ViewAs::sitePathOffset(). On a root-hosted site that is 0, so '/catalog/build/1' is a page;
 * under '/shop' it is 5, so '/shop/build/x.js' is an asset and '/shop/catalog/build/1' is not.
 *
 * Without this the JS test was a bare /^\/(?:build|local_assets|uploads)\//, which on a
 * subdirectory site disagreed with PHP in BOTH directions at once: '/shop/build/x.js' was tagged
 * as an application request, and '/build/1' - a catalog page of a site hosted at the root of a
 * host that also serves /shop - was refused one (#1509 review).
 *
 * @param {string} pathname
 * @returns {number}
 */
function sitePathOffset(pathname) {
    var base = sitePathBase();

    if (base === '') {
        return 0;
    }

    return pathname.indexOf(base + '/') === 0 ? base.length : 0;
}

function isAsset(url) {
    var pathname;

    try {
        pathname = new URL(url, window.location.href).pathname;
    } catch {
        return false;
    }

    var offset = sitePathOffset(pathname);

    return ASSET_PREFIXES.some(function (prefix) {
        return pathname.indexOf(prefix) === offset;
    });
}

/**
 * Is $url an application request of this site - one the context should ride on?
 *
 * @param {*} url
 * @returns {boolean}
 */
export function shouldCarryViewContext(url) {
    var u = typeof url === 'string' ? url : '';

    return !(
        u === '' ||
        /^#/.test(u) ||
        /^(?:tel|mailto|javascript|data|sms|blob):/i.test(u) ||
        isExternal(u) ||
        isAsset(u)
    );
}

/**
 * $url carrying the active context: `vc=<id>` added or replaced, fragment preserved, the id
 * encoded once. Unchanged when no context is active or the url is not an application request.
 *
 * @param {string} url
 * @param {string} [id] a specific context, for the rare caller that has one; defaults to the page's
 * @returns {string}
 */
export function withViewContext(url, id) {
    var contextId = typeof id === 'string' ? id : getViewContextId();

    if (!contextId || !shouldCarryViewContext(url)) {
        return url;
    }

    var fragment = '';
    var hash = url.indexOf('#');

    if (hash !== -1) {
        fragment = url.slice(hash);
        url = url.slice(0, hash);
    }

    var pair = VIEW_CONTEXT_PARAM + '=' + encodeURIComponent(contextId);
    var q = url.indexOf('?');

    if (q === -1) {
        return url + '?' + pair + fragment;
    }

    var base = url.slice(0, q);
    var query = url.slice(q + 1);
    var kept = [];

    if (query !== '') {
        query.split('&').forEach(function (part) {
            var key = part.split('=')[0];

            try {
                key = decodeURIComponent(key);
            } catch {
                // a key the browser could not decode is still not ours
            }

            if (key !== VIEW_CONTEXT_PARAM) {
                kept.push(part);
            }
        });
    }

    kept.push(pair);

    return base + '?' + kept.join('&') + fragment;
}

/**
 * The refusal reason on a failed XHR, or '' when the failure was something else (#1514).
 *
 * ViewAsExceptionHandler answers an AJAX consumer with 410 and
 * `{"view_as":"refused","reason":"ended|unknown|foreign|expired|ambiguous|stale_form"}`. A
 * caller that saves work in the background has to be able to tell that shape apart from a
 * network blip, because the two deserve opposite treatments: a blip may be retried, a refused
 * context never may. The view is gone, and re-sending the request would either be replayed
 * into a different view or answered as the staff member - which is exactly what the context
 * engine exists to prevent. So callers report this and stop.
 *
 * ONLY A 410 IS A REFUSAL. The same body with reason 'unavailable' arrives as HTTP 503 (#1744)
 * when the server could not READ the session - another request of the login held the session
 * lock past its timeout - and means the opposite: the view is intact and the request may be
 * asked again. It returns '' here on purpose, so every caller's "refused, stop" branch is
 * skipped and its ordinary failure handling (the idle poll's next tick, the draft's backoff)
 * runs. Do not widen this to match on the body alone.
 *
 * @param {object} jqXHR
 * @returns {string}
 */
export function viewContextRefusal(jqXHR) {
    if (!jqXHR || jqXHR.status !== 410) {
        return '';
    }

    var body = jqXHR.responseJSON;

    if (!body && typeof jqXHR.responseText === 'string' && jqXHR.responseText !== '') {
        try {
            body = JSON.parse(jqXHR.responseText);
        } catch {
            return '';
        }
    }

    if (!body || body.view_as !== 'refused') {
        return '';
    }

    return typeof body.reason === 'string' && body.reason !== '' ? body.reason : 'ended';
}

var endedReported = false;

/**
 * Tell the person, ONCE, that the customer view this page belongs to has ended (#1514).
 *
 * Once, because a page under a context makes several requests and every one of them is refused
 * from the same moment: a message per XHR would be a stack of identical dialogs. Callers report
 * and then stop - see the header on ordering/checkout-draft.js for why nothing may be retried
 * or re-sent.
 *
 * @returns {boolean} whether this call was the one that reported it
 */
export function reportViewContextEnded() {
    if (endedReported) {
        return false;
    }

    endedReported = true;

    showAlert({
        title: 'This customer view has ended',
        text:
            'Anything you have typed on this page is no longer being saved, and it cannot be ' +
            'submitted from here. Start a new customer view to continue.',
        icon: 'warning',
        confirmButtonText: 'OK',
    });

    return true;
}

/**
 * Report a failed XHR as an ended customer view if that is what it was, and say whether it was
 * (#1715).
 *
 * For an error handler that would otherwise show its own message. A 410 view-as refusal is not
 * the caller's failure: the view the page belongs to has ended, every later request is refused
 * the same way, and a "please try again" can never come true. So on a refusal this reports once
 * through reportViewContextEnded() and returns true, and the caller skips its own message - but
 * still undoes whatever optimistic change it made, and never re-sends. Any other failure returns
 * false and reports nothing.
 *
 * @param {object} jqXHR
 * @returns {boolean} whether jqXHR was a view-as refusal
 */
export function reportIfViewContextRefused(jqXHR) {
    if (!viewContextRefusal(jqXHR)) {
        return false;
    }

    reportViewContextEnded();

    return true;
}

function escapeAttribute(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * The hidden field a GET form needs to keep the context, as markup, or '' without a context.
 *
 * @param {string} [id]
 * @returns {string}
 */
export function viewContextHiddenField(id) {
    var contextId = typeof id === 'string' ? id : getViewContextId();

    if (!contextId) {
        return '';
    }

    return '<input type="hidden" name="' + VIEW_CONTEXT_PARAM + '" value="' + escapeAttribute(contextId) + '">';
}

var prefilterInstalled = false;

/**
 * Tag every same-origin jQuery XHR with the active context. Installed once, from
 * core/shell.js; a page that renders no context id is untouched by it.
 *
 * ajaxPrefilter runs before jQuery appends `data` to a GET url, so the parameter lands in the
 * query string either way - which is where the server reads it (user_helper.php), never from
 * the POST body.
 */
export function installViewContextAjaxPrefilter() {
    if (prefilterInstalled) {
        return;
    }

    prefilterInstalled = true;

    $.ajaxPrefilter(function (options) {
        if (!options || typeof options.url !== 'string') {
            return;
        }

        options.url = withViewContext(options.url);
    });
}
